import React, { useRef, useState } from 'react';

interface FileUploadProps {
  label: string;
  onFileUpload: (file: File) => void;
  accept?: string;
  disabled?: boolean;
  maxSizeMB?: number;
}

const DEFAULT_MAX_SIZE_MB = 10;

const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  'image/*': ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  'application/pdf': ['application/pdf'],
  '.pdf': ['application/pdf'],
  'video/*': ['video/mp4', 'video/webm', 'video/ogg'],
  'audio/*': ['audio/mpeg', 'audio/wav', 'audio/ogg'],
};

function isValidMimeType(file: File, accept: string): boolean {
  const acceptTypes = accept.split(',').map(t => t.trim());

  for (const acceptType of acceptTypes) {
    if (acceptType === '*/*') return true;

    const allowedMimes = ALLOWED_MIME_TYPES[acceptType];
    if (allowedMimes && allowedMimes.includes(file.type)) {
      return true;
    }

    if (acceptType.endsWith('/*')) {
      const category = acceptType.replace('/*', '');
      if (file.type.startsWith(category + '/')) {
        return true;
      }
    }

    if (file.type === acceptType) {
      return true;
    }
  }

  return false;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  onFileUpload,
  accept = 'image/*',
  disabled,
  maxSizeMB = DEFAULT_MAX_SIZE_MB
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError(null);

    if (file) {
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setError(`File too large. Maximum size is ${maxSizeMB}MB.`);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      if (!isValidMimeType(file, accept)) {
        setError('Invalid file type. Please select a valid file.');
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      setFileName(file.name);
      onFileUpload(file);
    }
  };

  return (
    <div className="flex flex-col">
      <label className="block text-sm font-medium text-neutral-700 mb-2">{label}</label>
      <div className="flex items-center space-x-2">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled}
          className="hidden"
        />
        <button
          type="button"
          className="border border-neutral-300 rounded-lg px-3 py-2 bg-white hover:bg-neutral-50 focus:ring-2 focus:ring-primary focus:border-transparent"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          {fileName ? 'Change File' : 'Choose File'}
        </button>
        {fileName && <span className="text-sm text-neutral-600">{fileName}</span>}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};
